import React, { useState, useEffect } from 'react';
import { MapPin, Heart, Star, Plane } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/DestinationGallery.css';

const DestinationGallery = ({ destinations, highlightedDestinations }) => {
  const { language, t, translateCountry, translateContinent } = useLanguage();
  const [visibleCards, setVisibleCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [randomizedDestinations, setRandomizedDestinations] = useState([]);
  
  // Randomize destinations on initial mount
  useEffect(() => {
    if (destinations.length > 0 && randomizedDestinations.length === 0) {
      const shuffled = [...destinations].sort(() => Math.random() - 0.5);
      setRandomizedDestinations(shuffled);
    }
  }, [destinations]);
  
  // Staggered animation on mount
  useEffect(() => {
    if (highlightedDestinations.length > 0) {
      setVisibleCards([]); // Reset visible cards
      highlightedDestinations.forEach((dest, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, dest.name]);
        }, index * 150);
      });
    }
  }, [highlightedDestinations]);
  
  // Generate image URL based on destination
  const getDestinationImage = (destination) => {
    // Use image_url from database if available
    if (destination.image_url) {
      return destination.image_url;
    }
    
    // Fallback to placeholder if no image_url
    return 'https://images.unsplash.com/photo-1519167758481-83f29da8c424?w=400&h=300&fit=crop';
  };
  
  const isHighlighted = (dest) => {
    return highlightedDestinations.some(h => h.name === dest.name);
  };
  
  const localTranslations = {
    de: {
      matchingDestinations: 'Passende Destinationen',
      allDestinations: 'Alle Destinationen',
      matchingCount: (count, total) => `${count} von ${total} Destinationen passen zu Ihrer Auswahl`,
      dreamLocations: (total) => `${total} traumhafte Hochzeitslocations weltweit`,
      topMatch: 'Top Match',
      goodMatch: 'Guter Match',
      possible: 'Möglich',
      nearbyOption: 'Naheliegende Option',
      answerQuestions: 'Beantworten Sie die Fragen, um Ihre perfekten Destinationen zu entdecken',
      personalizedSelection: 'Personalisierte Auswahl',
      destinations: 'Destinationen'
    },
    en: {
      matchingDestinations: 'Matching Destinations',
      allDestinations: 'All Destinations',
      matchingCount: (count, total) => `${count} of ${total} destinations match your selection`,
      dreamLocations: (total) => `${total} dream wedding locations worldwide`,
      topMatch: 'Top Match',
      goodMatch: 'Good Match',
      possible: 'Possible',
      nearbyOption: 'Nearby Option',
      answerQuestions: 'Answer the questions to discover your perfect destinations',
      personalizedSelection: 'Personalized Selection',
      destinations: 'Destinations'
    },
    es: {
      matchingDestinations: 'Destinos Coincidentes',
      allDestinations: 'Todos los Destinos',
      matchingCount: (count, total) => `${count} de ${total} destinos coinciden con tu selección`,
      dreamLocations: (total) => `${total} destinos de boda de ensueño en todo el mundo`,
      topMatch: 'Top Match',
      goodMatch: 'Buen Match',
      possible: 'Posible',
      nearbyOption: 'Opción Cercana',
      answerQuestions: 'Responde las preguntas para descubrir tus destinos perfectos',
      personalizedSelection: 'Selección Personalizada',
      destinations: 'Destinos'
    }
  };

  const tLocal = localTranslations[language] || localTranslations['de'];

  // Helper to translate climate values
  const translateClimate = (climate) => {
    const climateMap = {
      de: {
        'tropisch & warm': 'Tropisch & Warm',
        'mediterran & mild': 'Mediterran & Mild',
        'gemäßigt & kühl': 'Gemäßigt & Kühl',
        'trocken & sonnig': 'Trocken & Sonnig',
        'alpin & polar': 'Alpin & Polar'
      },
      en: {
        'tropisch & warm': 'Tropical & Warm',
        'mediterran & mild': 'Mediterranean & Mild',
        'gemäßigt & kühl': 'Temperate & Cool',
        'trocken & sonnig': 'Dry & Sunny',
        'alpin & polar': 'Alpine & Polar'
      },
      es: {
        'tropisch & warm': 'Tropical y Cálido',
        'mediterran & mild': 'Mediterráneo y Suave',
        'gemäßigt & kühl': 'Templado y Fresco',
        'trocken & sonnig': 'Seco y Soleado',
        'alpin & polar': 'Alpino y Polar'
      }
    };
    
    return climateMap[language]?.[climate] || climateMap['de'][climate] || climate;
  };

  // Show more destinations in compact mode during quiz
  const displayDestinations = highlightedDestinations.length > 0 
    ? highlightedDestinations 
    : randomizedDestinations.slice(0, 24); // Show 24 randomized destinations

  const totalDestinations = destinations.length;
  const highlightedCount = highlightedDestinations.length;
  
  return (
    <div className="destination-gallery-container">
      <div className="gallery-header">
        <div className="header-icon">
          <Plane size={20} />
        </div>
        <div className="header-content">
          <h2 className="gallery-title">
            {highlightedCount > 0 ? tLocal.matchingDestinations : tLocal.allDestinations}
          </h2>
          <p className="gallery-subtitle">
            {highlightedCount > 0 
              ? tLocal.matchingCount(highlightedCount, totalDestinations)
              : tLocal.dreamLocations(totalDestinations)}
          </p>
        </div>
      </div>

      <div className="polaroid-grid">
        {displayDestinations.map((dest, index) => {
          const highlighted = isHighlighted(dest);
          const isVisible = visibleCards.includes(dest.name) || highlightedDestinations.length === 0;
          
          return (
            <div
              key={index}
              className={`polaroid-card ${highlighted ? 'highlighted' : ''} ${highlighted ? dest.match_quality || 'perfect' : ''} ${isVisible ? 'visible' : ''}`}
              onClick={() => setSelectedCard(dest)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Ribbon für Match-Qualität */}
              {highlighted && (
                <div className={`match-ribbon ${dest.match_quality || 'perfect'}`}>
                  <Star size={12} fill="white" />
                  <span>
                    {dest.match_quality === 'perfect' && tLocal.topMatch}
                    {dest.match_quality === 'good' && tLocal.goodMatch}
                    {dest.match_quality === 'possible' && tLocal.possible}
                    {dest.match_quality === 'nearby' && tLocal.nearbyOption}
                  </span>
                </div>
              )}
              
              {/* Polaroid Frame */}
              <div className="polaroid-frame">
                <div className="polaroid-image">
                  <img 
                    src={getDestinationImage(dest)} 
                    alt={dest.name}
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1519167758481-83f29da8c424?w=400&h=300&fit=crop';
                    }}
                  />
                </div>
                
                {/* Polaroid Caption */}
                <div className="polaroid-caption">
                  <div className="caption-top">
                    <h3 className="destination-name">{translateCountry(dest.name, language)}</h3>
                    <Heart 
                      size={16} 
                      className={`heart-icon ${highlighted ? 'filled' : ''}`}
                      fill={highlighted ? '#cb9ac5' : 'none'}
                    />
                  </div>
                  <div className="caption-details">
                    <span className="continent-tag">{translateContinent(dest.continent, language)}</span>
                    {dest.climate && Array.isArray(dest.climate) && dest.climate.length > 0 && (
                      <span className="climate-tag">
                        {dest.climate.map((c, i) => (
                          <React.Fragment key={i}>
                            {translateClimate(c)}
                            {i < dest.climate.length - 1 && ' • '}
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Pulse Effect für Highlighted */}
              {highlighted && <div className="pulse-effect"></div>}
            </div>
          );
        })}
      </div>
      
      {/* Bottom Info */}
      {highlightedDestinations.length === 0 && (
        <div className="gallery-footer">
          <p className="footer-text">
            ✨ {tLocal.answerQuestions}
          </p>
        </div>
      )}
      
      {highlightedDestinations.length > 0 && (
        <div className="gallery-stats">
          <div className="stat-item">
            <MapPin size={18} />
            <span>{highlightedDestinations.length} {tLocal.destinations}</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <Star size={18} />
            <span>{tLocal.personalizedSelection}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinationGallery;
